<script setup lang="ts">
import { computed } from "vue";

import { ATTAINMENT_LABEL, type AttainmentTier } from "@/lib/attainment";

/**
 * Knowledge attainment ring (PRD §6.3).
 *
 * **Rings are knowledge. Trees are focus.** Never render a ring for a focus
 * stage or a tree for a knowledge metric (PE-7.3.6).
 *
 * "Needs review" is the only tier drawn on a dashed track: the progress is
 * still there, the freshness is not. That distinction is the whole reason the
 * tier exists, so it has to be visible at a glance rather than only in the
 * label.
 */

const props = withDefaults(
  defineProps<{
    /** 0–100. Rendered as 0 for `not_started`. */
    pct: number;
    tier: AttainmentTier;
    size?: number;
    /** Show the percentage inside the ring. Off for the small row rings. */
    showValue?: boolean;
  }>(),
  { size: 28, showValue: false },
);

// A 0–100 dasharray means `pct` is the dash length directly, so the ring needs
// no per-size circumference maths.
const RADIUS = 100 / (2 * Math.PI);
const DIAMETER = RADIUS * 2;

const filled = computed(() =>
  props.tier === "not_started" ? 0 : Math.min(100, Math.max(0, props.pct)),
);
const strokeWidth = computed(() => (props.size >= 56 ? 7 : 9));
const label = computed(
  () => `${ATTAINMENT_LABEL[props.tier]} — ${Math.round(props.pct)}%`,
);
</script>

<template>
  <div class="ring-wrap" :style="{ width: `${size}px`, height: `${size}px` }">
    <svg
      :viewBox="`0 0 ${DIAMETER + 12} ${DIAMETER + 12}`"
      :width="size"
      :height="size"
      role="img"
      :aria-label="label"
      :data-tier="tier"
    >
      <g
        :transform="`translate(${(DIAMETER + 12) / 2} ${(DIAMETER + 12) / 2}) rotate(-90)`"
      >
        <circle
          class="track"
          :class="{ 'track-stale': tier === 'needs_review' }"
          :r="RADIUS"
          fill="none"
          :stroke-width="strokeWidth"
        />
        <circle
          v-if="filled > 0"
          class="fill"
          :r="RADIUS"
          fill="none"
          :stroke-width="strokeWidth"
          stroke-linecap="round"
          :stroke-dasharray="`${filled} 100`"
        />
      </g>
    </svg>
    <span v-if="showValue" class="ring-value"
      >{{ Math.round(pct) }}<span class="pct">%</span></span
    >
  </div>
</template>

<style scoped>
.ring-wrap {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
}

.track {
  stroke: var(--rl-bg-muted);
}

/* Dashed track = the progress is real but stale. */
.track-stale {
  stroke: var(--rl-border-strong);
  stroke-dasharray: 4 3;
}

.fill {
  stroke: var(--rl-green-500);
}

[data-tier="learning"] .fill {
  stroke: var(--rl-warn-fg);
}

[data-tier="secure"] .fill {
  stroke: var(--rl-green-700);
}

.ring-value {
  position: absolute;
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-lg);
  font-weight: 600;
  letter-spacing: -0.03em;
}

.pct {
  font-size: 0.62em;
  color: var(--rl-fg-3);
}
</style>
